// Iteration 098 -- Netball scoring page.
// Parallel to rugby-match-score.tsx. Deliberately duplicated -- each sport
// owns its own scoring pad (feedback_sport_scoring_no_abstraction.md).
import { useCallback, useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import type {
  NetballMatchResultPublic,
  NetballScoreEventPublic,
  NetballMatchResultTeamSummary,
  NetballScoreResponse,
  TeamSide,
  NetballScoreEventType,
} from "@dugads/shared";
import { NETBALL_SCORING } from "@dugads/shared";
import * as api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { OfflineScoreBar } from "@/components/offline/offline-score-bar";
import { Undo2, Lock, Unlock } from "lucide-react";

/**
 * Quarter label for the scoreboard badge. 0 = Pre-match, 1..4 = live Q,
 * 5 = Full time.
 */
function quarterLabel(q: number, final: boolean): string {
  if (final || q >= 5) return "FT";
  if (q === 0) return "Pre-match";
  return `Q${q}`;
}

/**
 * Netball match clock: maps wall-clock time into quarter windows using
 * `starts_at` + `quarter_length_mins`. Break between quarters taken from
 * NETBALL_SCORING.period_structure.break_length_mins.
 *
 * Returns the quarter currently in-play (1..4), or 0 before Q1 and 5 once
 * all four quarters + breaks are done. Does not mutate the server -- the
 * recorder still picks the quarter for each event so the UI can disagree
 * with the server gracefully (e.g. the match starts late).
 */
function currentNetballQuarter(result: NetballMatchResultPublic): number {
  const startsMs = new Date(result.starts_at).getTime();
  const now = Date.now();
  if (now < startsMs) return 0;
  const qLen = result.quarter_length_mins * 60_000;
  const brLen = NETBALL_SCORING.period_structure.break_length_mins * 60_000;
  let t = startsMs;
  for (let q = 1; q <= 4; q += 1) {
    if (now < t + qLen) return q;
    t += qLen;
    if (q < 4) {
      if (now < t + brLen) return q; // mid-break, still "current" quarter just finished
      t += brLen;
    }
  }
  return 5;
}

const NETBALL_SCORE_BUTTONS: Array<{
  type: Exclude<NetballScoreEventType, "correction" | "yellow_card" | "red_card">;
  label: string;
  className: string;
}> = [
  { type: "goal", label: "Goal", className: "bg-green-600 hover:bg-green-700 text-white" },
  { type: "missed_goal", label: "Miss", className: "bg-muted hover:bg-muted/70 text-foreground" },
  { type: "intercept", label: "Intercept", className: "bg-sky-500 hover:bg-sky-600 text-white" },
];

export default function NetballMatchScorePage() {
  const { id } = useParams<{ id: string }>();
  const [data, setData] = useState<NetballScoreResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  // Recorder-selected quarter -- defaults to schedule-derived current quarter
  // on load but sticks with what the recorder picks so they can backfill.
  const [quarter, setQuarter] = useState<1 | 2 | 3 | 4>(1);

  const reload = useCallback(() => {
    if (!id) return;
    setLoading(true);
    api
      .getNetballMatchScore(id)
      .then((res) => {
        setData(res);
        // Initialise the recorder's quarter selector only on first load.
        setQuarter((prev) => {
          if (prev !== 1) return prev;
          const derived = currentNetballQuarter(res.result);
          return (derived >= 1 && derived <= 4 ? derived : 1) as 1 | 2 | 3 | 4;
        });
      })
      .catch((e: unknown) => setError(e instanceof api.ApiError ? e.message : "Failed to load"))
      .finally(() => setLoading(false));
  }, [id]);

  useEffect(() => {
    reload();
  }, [reload]);

  async function record(
    side: TeamSide,
    eventType: Exclude<NetballScoreEventType, "correction">,
  ) {
    if (!id) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await api.recordNetballScoreEvent(id, {
        team_side: side,
        event_type: eventType,
        quarter,
      });
      setData(res);
    } catch (e) {
      if (e instanceof api.QueuedOfflineError) return; // saved offline; bar shows it
      setError(e instanceof api.ApiError ? e.message : "Failed to record event");
    } finally {
      setSubmitting(false);
    }
  }

  async function undo(eventId: string) {
    if (!id) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await api.deleteNetballScoreEvent(id, eventId);
      setData(res);
    } catch (e) {
      if (e instanceof api.QueuedOfflineError) return;
      setError(e instanceof api.ApiError ? e.message : "Failed to undo");
    } finally {
      setSubmitting(false);
    }
  }

  async function finalize() {
    if (!id) return;
    if (!confirm("Mark this netball match as final?")) return;
    setSubmitting(true);
    try {
      const res = await api.finalizeNetballMatchScore(id);
      setData(res);
    } catch (e) {
      if (e instanceof api.QueuedOfflineError) return;
      setError(e instanceof api.ApiError ? e.message : "Failed to finalize");
    } finally {
      setSubmitting(false);
    }
  }

  async function reopen() {
    if (!id) return;
    setSubmitting(true);
    try {
      const res = await api.reopenNetballMatchScore(id);
      setData(res);
    } catch (e) {
      setError(e instanceof api.ApiError ? e.message : "Failed to reopen");
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) return <p data-page="netball-match-score" data-state="loading">Loading…</p>;
  if (!data) return <p data-page="netball-match-score" data-state="empty">No score data</p>;

  const { result, events } = data;
  const canRecord = result._actions.can_record;
  const canFinalize = result._actions.can_finalize;
  const canCorrect = result._actions.can_correct;
  const currentQ = quarterLabel(result.current_quarter, result.final);

  const homeEvents = events.filter((e) => e.team_side === "home");
  const awayEvents = events.filter((e) => e.team_side === "away");

  return (
    <div className="flex flex-col gap-4" data-page="netball-match-score">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="min-w-0">
          <Link to={`/matches/${id}`} className="text-sm text-muted-foreground hover:underline">
            ← Back to match
          </Link>
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="text-2xl font-semibold">
              {result.age_group ? `${result.age_group} · Netball` : "Netball"}
            </h1>
            <span
              className={`inline-flex items-center rounded px-2 py-0.5 text-xs font-semibold uppercase tracking-wide ${
                result.final
                  ? "bg-muted text-muted-foreground"
                  : "bg-emerald-600 text-white"
              }`}
              data-status={result.final ? "final" : "live"}
            >
              {currentQ}
            </span>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <NetballScoreboard
            home={result.home_team}
            away={result.away_team}
            homeScore={result.home_score}
            awayScore={result.away_score}
          />
          {result.final && canCorrect && (
            <Button variant="outline" size="sm" onClick={reopen} disabled={submitting}>
              <Unlock className="mr-2 h-4 w-4" />
              Reopen
            </Button>
          )}
          {!result.final && canFinalize && (
            <Button variant="outline" size="sm" onClick={finalize} disabled={submitting}>
              <Lock className="mr-2 h-4 w-4" />
              Finalise
            </Button>
          )}
        </div>
      </div>

      {error && <p className="text-sm text-destructive" data-error>{error}</p>}
      <OfflineScoreBar matchId={id} />
      {result.final && (
        <div className="rounded border bg-muted px-3 py-2 text-sm">Match finalised -- scores locked.</div>
      )}

      {/* Quarter selector -- recorders pick which quarter to score into. */}
      {canRecord && (
        <div className="flex items-center gap-2" data-field="quarter-selector">
          <span className="text-sm font-medium text-muted-foreground">Quarter:</span>
          {([1, 2, 3, 4] as const).map((q) => (
            <button
              key={q}
              type="button"
              onClick={() => setQuarter(q)}
              data-action={`select-quarter-${q}`}
              className={`inline-flex h-8 w-10 items-center justify-center rounded-md text-sm font-semibold ${
                quarter === q
                  ? "bg-primary text-primary-foreground"
                  : "bg-muted text-muted-foreground hover:bg-muted/70"
              }`}
            >
              Q{q}
            </button>
          ))}
        </div>
      )}

      {/* Per-quarter breakdown row */}
      <div className="grid grid-cols-[auto_repeat(4,1fr)_auto] items-center gap-2 text-sm tabular-nums" data-field="quarter-breakdown">
        <span className="font-medium text-muted-foreground">Home</span>
        {([1, 2, 3, 4] as const).map((q) => (
          <span key={`home-q${q}`} className="rounded bg-muted px-2 py-1 text-center">
            {result.home_breakdown[`q${q}` as keyof typeof result.home_breakdown]}
          </span>
        ))}
        <span className="px-2 py-1 text-center font-bold">{result.home_score}</span>
        <span className="font-medium text-muted-foreground">Away</span>
        {([1, 2, 3, 4] as const).map((q) => (
          <span key={`away-q${q}`} className="rounded bg-muted px-2 py-1 text-center">
            {result.away_breakdown[`q${q}` as keyof typeof result.away_breakdown]}
          </span>
        ))}
        <span className="px-2 py-1 text-center font-bold">{result.away_score}</span>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <NetballTeamPanel
          side="home"
          team={result.home_team}
          events={homeEvents}
          canRecord={canRecord}
          submitting={submitting}
          onRecord={(type) => record("home", type)}
          onUndo={undo}
        />
        <NetballTeamPanel
          side="away"
          team={result.away_team}
          events={awayEvents}
          canRecord={canRecord}
          submitting={submitting}
          onRecord={(type) => record("away", type)}
          onUndo={undo}
        />
      </div>
    </div>
  );
}

// ─── Netball scoreboard (simple -- no flash pulses yet, SSE is rugby-only) ───

interface NetballScoreboardProps {
  home: NetballMatchResultTeamSummary | null;
  away: NetballMatchResultTeamSummary | null;
  homeScore: number;
  awayScore: number;
}

export function NetballScoreboard({ home, away, homeScore, awayScore }: NetballScoreboardProps) {
  return (
    <div
      className="inline-flex items-stretch overflow-hidden rounded border border-border bg-card"
      data-field="netball-scoreboard"
    >
      <SidePill team={home} score={homeScore} align="right" />
      <div className="flex items-center bg-muted px-2 text-xs font-semibold uppercase text-muted-foreground">
        vs
      </div>
      <SidePill team={away} score={awayScore} align="left" />
    </div>
  );
}

function SidePill({
  team,
  score,
  align,
}: {
  team: NetballMatchResultTeamSummary | null;
  score: number;
  align: "left" | "right";
}) {
  const style: React.CSSProperties = {
    backgroundColor: team?.school_primary_color ?? undefined,
    color: team?.school_secondary_color ?? undefined,
  };
  return (
    <div
      className={`flex min-w-[6rem] items-center gap-2 px-3 py-1.5 ${
        align === "right" ? "flex-row-reverse" : ""
      }`}
      style={style}
    >
      <span className="text-lg font-bold tabular-nums">{score}</span>
      <span className="truncate text-sm font-semibold">
        {team?.school_abbreviation ?? team?.team_display_name ?? "--"}
      </span>
    </div>
  );
}

// ─── Team panel ─────────────────────────────────────────────────────

interface NetballTeamPanelProps {
  side: TeamSide;
  team: NetballMatchResultTeamSummary | null;
  events: NetballScoreEventPublic[];
  canRecord: boolean;
  submitting: boolean;
  onRecord: (eventType: Exclude<NetballScoreEventType, "correction">) => void;
  onUndo: (eventId: string) => void;
}

function NetballTeamPanel({ side, team, events, canRecord, submitting, onRecord, onUndo }: NetballTeamPanelProps) {
  return (
    <Card data-side={side} className="relative flex flex-col">
      <CardContent className="flex flex-col gap-4 pt-5">
        <div className="flex items-baseline justify-between gap-2">
          <span className="truncate text-sm font-semibold uppercase tracking-wide text-muted-foreground" data-field={`team-name-${side}`}>
            {team?.team_display_name ?? (side === "home" ? "Home" : "Away")}
          </span>
          {team?.school_abbreviation && (
            <span className="text-xs text-muted-foreground">{team.school_abbreviation}</span>
          )}
        </div>

        {canRecord && (
          <>
            <div className="grid grid-cols-3 gap-2">
              {NETBALL_SCORE_BUTTONS.map((b) => (
                <button
                  key={b.type}
                  type="button"
                  disabled={submitting}
                  onClick={() => onRecord(b.type)}
                  data-action={`score-${side}-${b.type}`}
                  className={`inline-flex h-11 items-center justify-center rounded-md px-3 text-sm font-semibold shadow-sm disabled:opacity-50 ${b.className}`}
                >
                  {b.label}
                </button>
              ))}
            </div>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                disabled={submitting}
                onClick={() => onRecord("yellow_card")}
                data-action={`score-${side}-yellow_card`}
                className="inline-flex items-center justify-center gap-2 rounded-md bg-amber-400 px-3 py-1.5 text-xs font-bold uppercase text-amber-900 shadow-sm hover:bg-amber-500 disabled:opacity-50"
              >
                <CardGlyph color="amber" />
                Yellow
              </button>
              <button
                type="button"
                disabled={submitting}
                onClick={() => onRecord("red_card")}
                data-action={`score-${side}-red_card`}
                className="inline-flex items-center justify-center gap-2 rounded-md bg-red-600 px-3 py-1.5 text-xs font-bold uppercase text-white shadow-sm hover:bg-red-700 disabled:opacity-50"
              >
                <CardGlyph color="red" />
                Red
              </button>
            </div>
          </>
        )}

        <div className="min-h-0 max-h-[50vh] overflow-y-auto transition-[max-height] duration-300 ease-out">
          <NetballTeamTimeline events={events} canUndo={canRecord} submitting={submitting} onUndo={onUndo} />
        </div>
      </CardContent>
    </Card>
  );
}

function NetballTeamTimeline({
  events,
  canUndo,
  submitting,
  onUndo,
}: {
  events: NetballScoreEventPublic[];
  canUndo: boolean;
  submitting: boolean;
  onUndo: (eventId: string) => void;
}) {
  if (events.length === 0) {
    return <p className="text-xs text-muted-foreground">No events yet.</p>;
  }
  return (
    <ul className="space-y-1 text-sm" data-field="team-timeline">
      {events.slice().reverse().map((e) => (
        <li
          key={e.id}
          className="flex items-center gap-2 rounded border border-transparent bg-muted/30 px-2 py-1 hover:border-border"
        >
          <span className="w-10 font-mono text-xs text-muted-foreground">Q{e.quarter}</span>
          <EventBadge eventType={e.event_type} />
          <span className="flex-1 capitalize truncate">{e.event_type.replace("_", " ")}</span>
          {e.points !== 0 && (
            <span className="font-bold tabular-nums">
              {e.points > 0 ? `+${e.points}` : e.points}
            </span>
          )}
          {canUndo && (
            <button
              type="button"
              onClick={() => onUndo(e.id)}
              disabled={submitting}
              title="Undo this event"
              className="rounded p-1 text-muted-foreground hover:bg-background hover:text-foreground disabled:opacity-40"
              data-action={`undo-${e.id}`}
            >
              <Undo2 className="h-3.5 w-3.5" />
            </button>
          )}
        </li>
      ))}
    </ul>
  );
}

function EventBadge({ eventType }: { eventType: NetballScoreEventPublic["event_type"] }) {
  if (eventType === "yellow_card") return <CardGlyph color="amber" />;
  if (eventType === "red_card") return <CardGlyph color="red" />;
  const map: Record<string, { bg: string; letter: string }> = {
    goal: { bg: "bg-green-600", letter: "G" },
    missed_goal: { bg: "bg-muted-foreground", letter: "M" },
    intercept: { bg: "bg-sky-500", letter: "I" },
    correction: { bg: "bg-muted-foreground", letter: "≈" },
  };
  const { bg, letter } = map[eventType] ?? { bg: "bg-muted-foreground", letter: "·" };
  return (
    <span
      className={`inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[10px] font-bold text-white ${bg}`}
      aria-hidden
    >
      {letter}
    </span>
  );
}

function CardGlyph({ color }: { color: "amber" | "red" }) {
  const bg = color === "amber" ? "bg-amber-400" : "bg-red-600";
  return <span className={`inline-block h-4 w-3 shrink-0 rounded-sm ${bg}`} aria-hidden />;
}
